interface HeroSectionProps {
  onGetFreeGuide: () => void;
}

export default function HeroSection({ onGetFreeGuide }: HeroSectionProps) {
  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col items-center justify-center bg-[#0a1628] overflow-hidden pt-20"
    >
      {/* Background graphic elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#f5a623]/5 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#f5a623]/5 blur-3xl" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[2px] bg-gradient-to-r from-transparent via-[#f5a623]/20 to-transparent" />
        {/* Grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'linear-gradient(#f5a623 1px, transparent 1px), linear-gradient(90deg, #f5a623 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-5xl mx-auto px-4 md:px-8 text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-[#f5a623]/10 border border-[#f5a623]/30 rounded-full px-4 py-2 mb-8">
          <i className="ri-trophy-fill text-[#f5a623] text-sm" />
          <span className="text-[#f5a623] font-body text-sm font-semibold uppercase tracking-widest">
            Scholarships. Exposure. Results.
          </span>
        </div>

        {/* Main headline */}
        <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white uppercase leading-none tracking-tight mb-6">
          Get Your Athlete
          <span className="block text-[#f5a623]">Recruited.</span>
        </h1>

        {/* Subheadline */}
        <p className="font-body text-lg md:text-xl lg:text-2xl text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed">
          The complete parent playbook — from{' '}
          <strong className="text-white font-semibold">Freshman year</strong> to{' '}
          <strong className="text-white font-semibold">Signing Day.</strong>
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <a
            href="https://stan.store/The_RecruitingPlaybook/p/get-the-free-7step-playbook-now"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto border-2 border-white/40 text-white px-8 py-4 rounded-md font-body text-base font-semibold hover:border-white/80 hover:bg-white/5 transition-all duration-200 whitespace-nowrap cursor-pointer text-center"
          >
            Get the Free Guide
          </a>
          <a
            href="https://stan.store/The_RecruitingPlaybook/p/get-the-recruiting-playbook-now"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto bg-[#f5a623] text-[#0a1628] px-8 py-4 rounded-md font-body text-base font-bold hover:bg-[#f7b84b] transition-all duration-200 whitespace-nowrap cursor-pointer text-center"
          >
            Get the Full Playbook — $47
          </a>
        </div>

        {/* Trust indicators */}
        <div className="flex flex-wrap items-center justify-center gap-6 mt-12 pt-12 border-t border-white/10">
          {[
            { icon: 'ri-shield-check-fill', label: '30-Day Money Back' },
            { icon: 'ri-download-2-fill', label: 'Instant Digital Download' },
            { icon: 'ri-parent-fill', label: 'Built for Parents' },
            { icon: 'ri-medal-2-fill', label: '500+ Families Helped' },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-[#f5a623] text-lg`} />
              </div>
              <span className="text-white/60 font-body text-sm font-medium whitespace-nowrap">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom wave */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" className="w-full h-12 md:h-16" preserveAspectRatio="none">
          <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#060e1a" />
        </svg>
      </div>
    </section>
  );
}
